import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import { mediaCategoryApiClient } from '@src/api/company/mediaCategory/mediaCategoryApiClient';
import { companySupportServiceCategoryApiClient } from '@src/api/company/supportService/supportServiceCategoryApiClient';
import { MenuCategory } from '@src/api/types/menuCategory';
import React, { createContext, useCallback, useContext, useState } from 'react';

type RawMediaCategory = {
  id: number;
  name: string;
  viewable: boolean;
  order: number;
  slug: string;
};

type RawSupportServiceCategory = {
  id: number;
  name: string;
  viewable: boolean;
  order: number;
  slug: string;
};

type FormattedCategory = {
  id: number;
  name: string;
  viewable: boolean;
  order: number;
  slug: string;
};

type CategoryContextType = {
  rawMediaCategories: RawMediaCategory[];
  rawSupportServiceCategories: RawSupportServiceCategory[];
  formattedMediaCategories: FormattedCategory[];
  formattedSupportServiceCategories: FormattedCategory[];
  menuMediaCategories: MenuCategory[];
  menuSupportServiceCategories: MenuCategory[];
  loadMediaCategories: () => Promise<void>;
  loadSupportServiceCategories: () => Promise<void>;
  selectedCategorySlug: string | undefined;
  setSelectedCategorySlug: (slug: string | undefined) => void;
};

const CategoryContext = createContext<CategoryContextType | undefined>(undefined);

const DEFAULT_MEDIA_CATEGORY: FormattedCategory = {
  id: 0,
  name: 'すべての記事',
  viewable: true,
  order: 0,
  slug: '',
};

const DEFAULT_SERVICE_CATEGORY: FormattedCategory = {
  id: 0,
  name: 'すべての支援サービス',
  viewable: true,
  order: 0,
  slug: '',
};

const filterViewableCategories = <T extends { viewable: boolean }>(categories: T[]): T[] => {
  return categories.filter((category) => category.viewable);
};

const formatCategories = <T extends RawMediaCategory | RawSupportServiceCategory>(
  categories: T[],
  defaultCategory: FormattedCategory,
): FormattedCategory[] => {
  const viewableCategories = filterViewableCategories(categories);
  return [defaultCategory, ...viewableCategories];
};

const convertToMenuCategories = (
  categories: FormattedCategory[],
  basePath: string,
  selectedSlug?: string,
): MenuCategory[] => {
  return categories.map((category) => ({
    to: category.slug ? `${basePath}?category_slug=${category.slug}` : basePath,
    label: category.name,
    isCurrent: selectedSlug ? selectedSlug === category.slug : !category.slug,
  }));
};

export const CategoryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rawMediaCategories, setRawMediaCategories] = useState<RawMediaCategory[]>([]);
  const [rawSupportServiceCategories, setRawSupportServiceCategories] = useState<
    RawSupportServiceCategory[]
  >([]);
  const [selectedCategorySlug, setSelectedCategorySlug] = useState<string>();

  const formattedMediaCategories = React.useMemo(
    () => formatCategories(rawMediaCategories, DEFAULT_MEDIA_CATEGORY),
    [rawMediaCategories],
  );

  const formattedSupportServiceCategories = React.useMemo(
    () => formatCategories(rawSupportServiceCategories, DEFAULT_SERVICE_CATEGORY),
    [rawSupportServiceCategories],
  );

  const menuMediaCategories = React.useMemo(
    () => convertToMenuCategories(formattedMediaCategories, '/articles', selectedCategorySlug),
    [formattedMediaCategories, selectedCategorySlug],
  );

  const menuSupportServiceCategories = React.useMemo(
    () =>
      convertToMenuCategories(
        formattedSupportServiceCategories,
        '/support-services',
        selectedCategorySlug,
      ),
    [formattedSupportServiceCategories, selectedCategorySlug],
  );

  const loadMediaCategories = useCallback(async () => {
    const res = await mediaCategoryApiClient.index();
    handleApiResponse(
      res,
      (data) => {
        setRawMediaCategories(data);
      },
      (error) => {
        console.error('Failed to fetch media categories:', error);
      },
    );
  }, []);

  const loadSupportServiceCategories = useCallback(async () => {
    const res = await companySupportServiceCategoryApiClient.index();
    handleApiResponse(
      res,
      (data) => {
        setRawSupportServiceCategories(data);
      },
      (error) => {
        console.error('Failed to fetch support service categories:', error);
      },
    );
  }, []);

  return (
    <CategoryContext.Provider
      value={{
        rawMediaCategories,
        rawSupportServiceCategories,
        formattedMediaCategories,
        formattedSupportServiceCategories,
        menuMediaCategories,
        menuSupportServiceCategories,
        loadMediaCategories,
        loadSupportServiceCategories,
        selectedCategorySlug,
        setSelectedCategorySlug,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (context === undefined) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};
